import { compareTableLabels } from './table-qr';

/**
 * Table board grouping (pure, unit-testable).
 *
 * The board shows open dine-in orders keyed by the table label snapshot taken
 * at order time, so editing a table never moves a live order. Orders within a
 * table are oldest-first (newest last), matching the on-screen reading order.
 */

export interface TableBoardOrderInput {
  id: string;
  order_number: number;
  order_type: string;
  status: string;
  table_label: string | null;
  payment_status: string;
  payment_method: string;
  total_cents: number;
  created_at: string;
  item_count: number;
}

export interface TableBoardGroup {
  table_label: string;
  /** Oldest first; the newest order is last. */
  orders: TableBoardOrderInput[];
  total_cents: number;
  item_count: number;
  unpaid_count: number;
  /** Created time of the oldest open order at the table. */
  opened_at: string;
}

/** Terminal statuses never belong on the board even if order_type is dine_in. */
const CLOSED_STATUSES = new Set(['completed', 'cancelled', 'refunded']);

export function isOpenDineInOrder(order: Pick<TableBoardOrderInput, 'order_type' | 'status'>): boolean {
  return order.order_type === 'dine_in' && !CLOSED_STATUSES.has(order.status);
}

export function groupTableBoardOrders(orders: TableBoardOrderInput[]): TableBoardGroup[] {
  const groups = new Map<string, TableBoardOrderInput[]>();
  for (const order of orders) {
    if (!isOpenDineInOrder(order)) continue;
    const label = (order.table_label ?? '').trim() || 'Unassigned';
    const list = groups.get(label) ?? [];
    list.push(order);
    groups.set(label, list);
  }

  return Array.from(groups.entries())
    .map(([table_label, list]) => {
      const sorted = list
        .slice()
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      const first = sorted[0];
      return {
        table_label,
        orders: sorted,
        total_cents: sorted.reduce((sum, order) => sum + order.total_cents, 0),
        item_count: sorted.reduce((sum, order) => sum + order.item_count, 0),
        unpaid_count: sorted.filter((order) => order.payment_status === 'unpaid').length,
        opened_at: first ? first.created_at : new Date(0).toISOString(),
      };
    })
    .sort((a, b) => compareTableLabels(a.table_label, b.table_label));
}
